<template>
  <v-app v-bind="{...$attrs, ...$props}" :class="{ 'core-is-impersonated': isImpersonated, 'is-student-mode': isStudentMode }">
    <client-only>
      <ConnectivityStatus />
    </client-only>

    <slot />

    <DialogDefault />

    <Snackbar />

    <ToastNotification v-if="isToastNotificationEnabled" />

    <Impersonated />
    <Notice v-if="$route.name === 'index'" />
    <student-mode-footer />
  </v-app>
</template>

<script>
import ConnectivityStatus from '@/components/_core/ui/connectivity-status';
import DialogDefault from '@/components/_core/ui/dialogs/default/index.vue';
import Impersonated from '@/components/_core/ui/impersonated/index.vue';
import Snackbar from '@/components/_core/ui/snackbar/index.vue';
import ToastNotification from '@/components/_core/ui/toast-notification/index.vue';
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import Notice from '@/components/ui/notice/index.vue';
import { EQUALWEB_STANDARD_TOKENS } from '@/helpers/equalweb.js';
import StudentModeFooter from '@/components/ui/student-mode-footer/index.vue';
import { buildDefaultAuthorizeUrl } from '~/helpers/_core/safea-v3';
import { SAFEA_V3_ENABLED } from '~/constants/_core/safea-v3';

Vue.config.ignoredElements = [
  /wc-*/
];

export default {
  name       : 'BaseLayout',
  components : {
    Snackbar,
    DialogDefault,
    Impersonated,
    Notice,
    ConnectivityStatus,
    StudentModeFooter,
    ToastNotification
  },
  head() {
    return {
      titleTemplate : `%s :: ${process.env.APPLICATION_TITLE}`,
      link          : [
        { rel: 'icon', type: 'image/x-icon', href: this.themes.favicon || `${process.env.APPLICATION_ROUTE_BASE}/favicon.ico`, id: '#favicon' }
      ],
      htmlAttrs: {
        lang: this.$i18n.locale
      }
    };
  },
  data() {
    return {
      tokenName                  : this.$token.getTokenName(),
      isToastNotificationEnabled : process.env.TOAST_NOTIFICATIONS_ENABLED === "true" && this.$route.query?.disableToastNotification !== "true"
    };
  },
  beforeMount() {
    const { accessibility, languageId } = this.$store.state._core.user;

    // Force Dark SSR
    setTimeout(() => {
      this.$store.commit('_core/application/REFRESH_THEME', accessibility.darkThemeEnabled);
    }, 100);

    this.$root.$i18n.locale = languageId || this.$core.getDefaultLocale();
    this.$root.$vuetify.lang.current = this.$root.$i18n.locale;
    if (!this.$store.app.$handTalk.existInstance()) {
      this.$store.app.$handTalk.start(accessibility.signLanguageEnabled);
    }

    window.addEventListener('focus', this.setTabFocus);
    window.addEventListener('storage', this.watchAccessToken);
    if (this.shouldUseContextMenu) {
      window.addEventListener('click', this.setNewTabOrigin);
      window.addEventListener('auxclick', this.setNewTabOrigin);
      window.addEventListener('contextmenu', this.setNewTabOrigin);
    }
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.setTabFocus);
    window.removeEventListener('storage', this.watchAccessToken);
    if (this.shouldUseContextMenu) {
      window.removeEventListener('click', this.setNewTabOrigin);
      window.removeEventListener('auxclick', this.setNewTabOrigin);
      window.removeEventListener('contextmenu', this.setNewTabOrigin);
    }
  },
  computed: {
    ...mapState({
      themes        : (state) => state._core.application.themes,
      isStudentMode : (state) => state.plataforma.user.isStudentMode
    }),
    ...mapGetters({
      isImpersonated: '_core/auth/isImpersonated'
    }),
    isIFrame() {
      return window.top !== window.self;
    },
    shouldUseContextMenu() {
      return this.$store.state._core.application.independentTabsEnabled || this.isIFrame;
    }
  },
  methods: {
    // Private functions to assist in the creation of context menu
    _getTabKeys(e) {
      return e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1;
    },
    _getComposedPath(e) {
      return e.path || (e.composedPath && e.composedPath());
    },
    _createMenuItemNode(label, action) {
      const node = document.createElement('li');

      node.textContent = label;
      node.onclick = e => {
        node.tabIndex = 1;
        node.focus();
        if (action) {
          action(e);
        }
      };

      return node;
    },
    _createDivider() {
      return document.createElement('hr');
    },
    _isSameV3Token(prev, curr) {
      if (!prev || !curr) {
        return false;
      }

      const prevToken = JSON.parse(prev);
      const currToken = JSON.parse(curr);
      return prevToken.refreshToken === currToken.refreshToken;
    },
    async watchAccessToken(e) {
      const hasChangedToken = e.key === this.tokenName;

      // watch for EqualWeb changes
      // only temporary, update in the next cockpit version
      const hasChangedEqualWebValues = EQUALWEB_STANDARD_TOKENS.includes(e.key);

      if (e.key !== this.$token.getTokenName() && e.key !== this.$token.getTokenNameV3() && !hasChangedEqualWebValues) {
        return;
      }

      if (e.key === this.$token.getTokenNameV3() && this._isSameV3Token(e.oldValue, e.newValue)) {
        return;
      }

      // This will only listen for local storage, not session ($token)
      const isSignInPage = this.$nuxt.$route.path === '/auth/signin';
      const independentTabsEnabled = this.$store.state._core.application.independentTabsEnabled;

      // Copies the token from local storage to session storage
      if (e.oldValue === null && e.newValue !== null && independentTabsEnabled) {
        if (SAFEA_V3_ENABLED && e.key === this.$token.getTokenNameV3()) {
          this.$session.setTokens(e.newValue, false);
        } else if (e.key === this.$token.getTokenName()) {
          this.$session.set(e.newValue, false);
        }
        return;
      }

      // No modals are shown in the login page
      // The user should refresh manually
      if (isSignInPage) {
        return;
      }

      if (hasChangedToken && e.newValue === null) {
        this.$token.clear();
        this.$dialog.show({
          type                 : 'error',
          title                : this.$t('_core.dialogs.token.loggedOut.title'),
          message              : this.$t('_core.dialogs.token.loggedOut.message'),
          confirmButtonText    : this.$t('_core.dialogs.token.loggedOut.confirmButtonText'),
          confirmButtonIcon    : 'mdi-reload',
          confirmButtonVariant : 'default',
          showCancelButton     : false,
          onConfirm            : async () => {
            this.$dialog.hide();
            this.$nuxt.$loading.start();

            const tokens = SAFEA_V3_ENABLED ? this.$token.getTokens() : this.$token.get();
            if (tokens === null) {
              if (SAFEA_V3_ENABLED) {
                const authorizeUrl = await buildDefaultAuthorizeUrl({
                  store : this.$store,
                  route : this.$route
                });
                window.location.assign(authorizeUrl);
              } else {
                await this.$store.dispatch('_core/auth/actionSignOut');
              }
            } else {
              // The user logged in another tab, just reload
              this.$router.go();
            }
          }
        });
      } else if (hasChangedToken && e.oldValue !== e.newValue && !independentTabsEnabled) {
        this.$dialog.show({
          type              : 'warning',
          showCancel        : false,
          title             : this.$t('_core.dialogs.token.changedRole.title'),
          message           : this.$t('_core.dialogs.token.changedRole.message'),
          confirmButtonText : this.$t('_core.dialogs.token.changedRole.confirmButtonText'),
          confirmButtonIcon : 'mdi-reload',
          showCancelButton  : false,
          onConfirm         : async () => {
            this.$dialog.hide();
            this.$nuxt.$loading.start();
            this.$router.go();
          }
        });
      } else if (hasChangedEqualWebValues) {
        // check if is the current tab
        // opens the modal only on the currently inactive tabs
        if (document.hidden) {
          this.$dialog.show({
            type              : 'warning',
            showCancel        : false,
            title             : this.$t('_core.dialogs.equalWeb.changedEqualWebValues.title'),
            message           : this.$t('_core.dialogs.equalWeb.changedEqualWebValues.message'),
            confirmButtonText : this.$t('_core.dialogs.equalWeb.changedEqualWebValues.confirmButtonText'),
            confirmButtonIcon : 'mdi-reload',
            showCancelButton  : false,
            onConfirm         : async () => {
              this.$dialog.hide();
              this.$nuxt.$loading.start();
              this.$router.go();
            }
          });
        }
      }
    },
    setNewTabOrigin(e) {
      const newTabKeys = this._getTabKeys(e);
      const urlOrigin = window.location.origin;
      const currentToken = this.$token.get();

      if (!currentToken) {
        return;
      }

      const composedPath = this._getComposedPath(e);

      for (const path of composedPath) {
        const isATag = path.tagName === 'A';
        const hasHref = !!path.href;

        if (isATag && hasHref && e.button === 2 && this.shouldUseContextMenu) {
          this.createLinkContextMenu(e, path);
          break;
        }

        if (isATag && (newTabKeys || path.target === '_blank')) {
          try {
            const pathUrl = new URL(path.href);
            if (pathUrl.origin === urlOrigin) {
              e.preventDefault();
              const handler = window.open(pathUrl.href, '_blank');
              handler.window.sessionStorage.setItem(this.$session.getTokenName(), currentToken);
              handler.window.localStorage.setItem(this.$token.getTokenName(), currentToken);
              break;
            }
          } catch (ignored) {}
        }
      }
    },
    setTabFocus() {
      this.$session.setTabFocus();
    },
    /**
     * @param {MouseEvent} e
     * @param {HTMLAnchorElement} target
     */
    async createLinkContextMenu(e, target) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const createMenu = (nodes) => {
        const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

        const baseStyles = document.createElement('style');
        baseStyles.innerText = `
          #iframe-contextmenu {
            z-index: 1000;
            position: fixed;

            gap: 4px;
            padding: 4px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;

            box-shadow: 2px 2px 4px 0px #878787;
            border: thin solid ${isDark ? '#3A3F42' : '#DADCE0'};
            background-color: ${isDark ? '#292A2D' : '#ffffff'};
          }
          #iframe-contextmenu hr {
            width: 100%;
            border: none;
            border-top: thin solid ${isDark ? '#3C4043' : '#DADCE0'};
          }
          #iframe-contextmenu li {
            display: flex;
            height: 22px;
            padding-left: 32px;
            padding-right: 16px;
            align-items: center;
            align-self: stretch;
            font-family: Arial, sans-serif;

            cursor: pointer;
            list-style-type: none;
            color: ${isDark ? 'white' : 'black'};
            background-color: ${isDark ? '#292A2D' : '#ffffff'};
          }
          #iframe-contextmenu li:hover {
            background-color: ${isDark ? '#3F4042' : '#E8E8E9'};
          }
        `;


        const baseNode = document.createElement('ul');
        baseNode.append(baseStyles);
        baseNode.append(...nodes);

        baseNode.id = 'iframe-contextmenu';
        baseNode.tabIndex = 1;
        baseNode.style.top = `${e.clientY + 1}px`;
        baseNode.style.left = `${e.clientX + 1}px`;

        baseNode.addEventListener('blur', () => baseNode.remove());

        document.body.append(baseNode);
        baseNode.focus();
      };

      if (process.server) {
        return;
      }

      const result = await window.navigator.permissions.query({ name: "clipboard-write" });

      createMenu([
        this._createMenuItemNode(this.$t('_core.contextmenu.openInNewTab'), () => {
          const currentToken = this.$token.get();
          const handler = window.open(target.href, '_blank');
          handler.window.sessionStorage.setItem(this.$session.getTokenName(), currentToken);
          handler.window.localStorage.setItem(this.$token.getTokenName(), currentToken);
        }),
        ...(result.state === 'denied' ? [] : [
          this._createDivider(),
          this._createMenuItemNode(this.$t('_core.contextmenu.copyLinkToClipboard'), async () => {
            await window.navigator.clipboard.writeText(target.href);
          })
        ])
      ]);
    }
  }
};
</script>
